import React, { useEffect, useState } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { resolveTenant, loadTenantConfig } from '@whitelabel/utils';

// Enhanced AuthProvider with smart tenant loading
//
// cacheLocation, useRefreshTokens and onRedirectCallback are passed STRAIGHT
// THROUGH to Auth0Provider and default to undefined, which is the SDK's own
// default and exactly what this component did before them (RCS-69). They are
// props rather than new defaults because this file is shared by the admin
// console, the web player and the WordPress-embedded player, and only the
// console has a session worth persisting - a listener signs in to nothing.
//
// The console passes cacheLocation="localstorage" because the SDK's default,
// 'memory', loses the token on every full page load and restores it through a
// hidden iframe to Auth0, which needs THIRD-PARTY COOKIES. Brave and Safari
// block those by default and Chrome is removing them, so on those browsers the
// restore can never succeed. See apps/admin/src/authCallback.ts for the other
// half of that failure.
export const AuthProvider = ({
  children,
  clientId,
  domain,
  audience,
  redirectUri,
  skipTenantConfig = false,
  cacheLocation,
  useRefreshTokens,
  useCookiesForTransactions,
  onRedirectCallback
}) => {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Detect tenant using existing shared logic
  const tenant = resolveTenant();
  const shouldLoadTenant = !skipTenantConfig && tenant && tenant !== 'default';

  useEffect(() => {
    const loadConfig = async () => {
      if (!shouldLoadTenant) {
        setConfigLoaded(true);
        return;
      }

      try {
        const config = await loadTenantConfig(tenant);
        setTenantConfig(config);
        setConfigLoaded(true);
      } catch (error) {
        console.error('[AuthProvider] Error loading tenant config:', error);
        setConfigLoaded(true);
      }
    };

    loadConfig();
  }, [shouldLoadTenant, tenant]);

  // Show loading while tenant config loads
  if (shouldLoadTenant && !configLoaded) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Loading tenant configuration...</div>
        <div style={{ fontSize: '14px', color: '#666' }}>Tenant: {tenant}</div>
      </div>
    );
  }

  const resolvedDomain = domain || tenantConfig?.auth0?.domain || process.env.REACT_APP_AUTH0_DOMAIN;
  const resolvedClientId = clientId || tenantConfig?.auth0?.clientId || process.env.REACT_APP_AUTH0_CLIENT_ID;

  console.log('[AuthProvider] Config:', {
    redirectUri,
    resolvedRedirectUri: redirectUri || tenantConfig?.auth0?.redirectUri || process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin,
    domain: resolvedDomain,
    clientId: resolvedClientId,
    audience
  });

  if (!resolvedDomain || !resolvedClientId) {
    console.log('[AuthProvider] No Auth0 credentials — running without authentication');
    return <>{children}</>;
  }

  return (
    <Auth0Provider
      domain={resolvedDomain}
      clientId={resolvedClientId}
      cacheLocation={cacheLocation}
      useRefreshTokens={useRefreshTokens}
      useCookiesForTransactions={useCookiesForTransactions}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: redirectUri || tenantConfig?.auth0?.redirectUri || process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin,
        audience: audience || tenantConfig?.auth0?.audience || process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      }}
    >
      {children}
    </Auth0Provider>
  );
};

// Export both names for compatibility
export const SharedAuthProvider = AuthProvider;
export default AuthProvider;
